import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";

import Preloader from "./components/Preloader";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import FloatingCart from "./components/FloatingCart";
import ChatBot from "./components/ChatBot";
import ScrollToTop from "./components/ScrollToTop";

import Home from "./pages/Home";
import BookSession from "./pages/BookSession";
import EventPackages from "./pages/EventPackages";
import Shop from "./pages/Shop";
import ShopProductDetails from "./pages/ShopProductDetails";
import Contact from "./pages/Contact";
import Rentals from "./pages/StudioRentals";
import Checkout from "./pages/Checkout";
import StudioSessionBookingSuccess from "./pages/StudioSessionBookingSuccess";
import StudioRentalPaymentSuccess from "./pages/StudioRentalPaymentSuccess";
import EventPaymentSuccess from "./pages/EventPaymentSuccess";
import ShopOrderSuccess from "./pages/ShopOrderSuccess";
import Gallery from "./pages/Gallery";
import Cart from "./pages/Cart";
import Printing from "./pages/Printing";
import PrintOrderSuccess from "./pages/PrintOrderSuccess";

import { CartProvider } from "./context/CartContext";

// ---- Inner app so we can read location (must be inside BrowserRouter) ----
function AppShell() {
  const location = useLocation();

  const [loading, setLoading] = useState(location.pathname === "/");
  const hasShownHomePreloader = useRef(false);

  useEffect(() => {
    if (location.pathname === "/" && !hasShownHomePreloader.current) {
      setLoading(true);
      const t = setTimeout(() => {
        setLoading(false);
        hasShownHomePreloader.current = true;
      }, 1800);
      return () => clearTimeout(t);
    }

    setLoading(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-black text-white">
      <ScrollToTop />

      <AnimatePresence mode="wait">
        {loading ? (
          <Preloader key="preloader" />
        ) : (
          <div key="site">
            <Navbar />

            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/book" element={<BookSession />} />
                <Route path="/packages" element={<EventPackages />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/:id" element={<ShopProductDetails />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/rentals" element={<Rentals />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/printing" element={<Printing />} />
                <Route
                  path="/studio-session-booking-success"
                  element={<StudioSessionBookingSuccess />}
                />
                <Route
                  path="/studio-rental-payment-success"
                  element={<StudioRentalPaymentSuccess />}
                />
                <Route
                  path="/event-payment-success"
                  element={<EventPaymentSuccess />}
                />
                <Route
                  path="/shop-order-success"
                  element={<ShopOrderSuccess />}
                />
                <Route
                  path="/print-order-success"
                  element={<PrintOrderSuccess />}
                />
              </Routes>
            </main>

            <Footer />
            <FloatingCart />
            <ChatBot />
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <CartProvider>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </CartProvider>
    </HelmetProvider>
  );
}
